import angular from 'angular';
import { ModalService, ModalServiceModule } from '@b2b/common/services';
import { ApiService } from '@b2b/common/services/api/types';
import { TODO, SalesChannelType } from '@b2b/types/core';
import { CartFormsModalsFilloutData, FormResponsePayload, FormResponseRecord, FormResponseRecordWithFormUUID } from '../types';
import { CartOrderForm } from '@b2b/types/cart';
import { CartEventOrderForm } from '@b2b/types/event';
import { CartMembershipOrderForm, isMembershipOrderForm, Membership } from '@b2b/types/membership';
import {
  filterEventFormsBySalesChannel,
  filterMembershipFormsBySalesChannel,
  isFormRequired,
  deduplicateFormsByDisplaySettings,
} from '../utils/form-filters.utils';

/**
 * Extended CartOrderForm with isRequired flag and related item names.
 * `relatedItemNames` holds all item names sharing this form (for "Multiple" display).
 */
export type CartOrderFormWithRequired = CartOrderForm & {
  isRequired: boolean;
  relatedItemNames: string[];
  uuid?: string; // Unique identifier for this form instance, used for tracking responses
};

/**
 * Sales channel type for form filtering
 */
export type SalesChannelPreference = 'pos' | 'b2c' | 'kiosk' | 'insideSales';

/**
 * Maps the component's SalesChannelPreference keys to the backend SalesChannelType values.
 * Defined at module level so it isn't reallocated on every filterFormsBySalesChannel call.
 */
const SALES_CHANNEL_MAP: Record<SalesChannelPreference, SalesChannelType> = {
  pos: 'pointOfSale',
  b2c: 'online',
  kiosk: 'kiosk',
  insideSales: 'insideSalesIndividual',
};

export class CartFormsService {
  static $inject = ['ApiService', 'ModalService'];

  constructor(private apiService: ApiService, private modalService: ModalService) {
    // noop
  }

  fetchFormDetails(formId: string) {
    return this.apiService.form.detail({ id: formId });
  }

  submitFormResponse(payload: FormResponsePayload) {
    return this.apiService.form.responses.create(payload);
  }

  updateFormResponse(id: string, payload: FormResponsePayload) {
    return this.apiService.form.responses.update({ ...payload, id });
  }

  private static readonly STORAGE_KEY = 'cart_form_responses';

  saveFormResponse(formUUid: string, response: FormResponseRecordWithFormUUID | undefined) {
    if (!response) {
      return;
    }

    response.formUUID = formUUid;
    const existing = this.getFormResponses();
    const updated = [...existing.filter((r) => r.formUUID !== response.formUUID), response];
    sessionStorage.setItem(CartFormsService.STORAGE_KEY, JSON.stringify(updated));
  }

  getFormResponses(): FormResponseRecordWithFormUUID[] {
    try {
      const raw = sessionStorage.getItem(CartFormsService.STORAGE_KEY);
      return raw ? (JSON.parse(raw) as FormResponseRecordWithFormUUID[]) : [];
    } catch {
      return [];
    }
  }

  clearFormResponses() {
    sessionStorage.removeItem(CartFormsService.STORAGE_KEY);
  }

  removeFormResponsesByFormIds(formIds: string[]) {
    if (!formIds.length) return;
    const existing = this.getFormResponses();
    const updated = existing.filter((r) => !formIds.includes(r.formId));
    sessionStorage.setItem(CartFormsService.STORAGE_KEY, JSON.stringify(updated));

    // Delete the API-side records for the removed responses
    const formIdSet = new Set(formIds);
    existing
      .filter((r) => formIdSet.has(r.formId))
      .forEach((r) => {
        this.apiService.form.responses.delete({ id: r.id });
      });
  }

  /**
   * Filter forms by sales channel preference and membership purchase type
   * Separates event forms and membership forms, applies appropriate filtering logic,
   * then deduplicates based on formDisplaySettings
   *
   * @param forms - Array of cart order forms to filter
   * @param memberships - Array of membership items in cart (needed for membership form filtering)
   * @param salesChannel - The sales channel to filter by (default: 'insideSales')
   * @returns Filtered array of forms with isRequired flag
   */
  filterFormsBySalesChannel(
    forms: CartOrderForm[],
    memberships: Membership[] = [],
    salesChannel: SalesChannelPreference = 'insideSales'
  ): CartOrderFormWithRequired[] {
    const mappedChannel = SALES_CHANNEL_MAP[salesChannel];

    // Separate event forms and membership forms
    const eventForms: CartEventOrderForm[] = [];
    const membershipForms: CartMembershipOrderForm[] = [];

    forms.forEach((form) => {
      if (form.itemType === 'event') {
        eventForms.push(form as CartEventOrderForm);
      } else if (isMembershipOrderForm(form)) {
        membershipForms.push(form);
      }
    });

    // Filter event forms using event-specific logic
    const filteredEventForms = filterEventFormsBySalesChannel(eventForms, salesChannel);

    // Filter membership forms using membership-specific logic (requires membership items)
    const filteredMembershipForms = filterMembershipFormsBySalesChannel(
      membershipForms,
      memberships,
      mappedChannel
    );

    // Combine filtered forms
    const allFilteredForms: CartOrderForm[] = [...filteredEventForms, ...filteredMembershipForms];

    // Deduplicate based on formDisplaySettings
    const deduplicatedForms = deduplicateFormsByDisplaySettings(allFilteredForms);

    // Add isRequired flag to each form
    return deduplicatedForms.map((form) => {
      // For event forms, use the original salesChannel parameter
      // For membership forms, use the mapped channel
      const channelToCheck = form.itemType === 'event' ? salesChannel : mappedChannel;

      return {
        ...form,
        isRequired: isFormRequired(form, channelToCheck),
      };
    });
  }

  /**
   * Returns true if any required form (after filtering by sales channel)
   * does not have a saved response in sessionStorage.
   */
  hasUnrespondedRequiredForms(forms: CartOrderForm[], memberships: Membership[] = []): boolean {
    const filteredForms = this.filterFormsBySalesChannel(forms, memberships, 'insideSales');
    const respondedIds = new Set(this.getFormResponses().map((r) => r.formUUID));
    return filteredForms.some((form) => form.isRequired && !respondedIds.has(form.uuid ?? ''));
  }

  openFormFilloutModal(form: CartOrderFormWithRequired, options?: { skipSaveInSessionStorage: boolean }) {
    const existingResponse = this.getFormResponses().find((r) => r.formUUID === form.uuid);
    return this.openFormFilloutModalWithData({
      form,
      existingResponse,
      relatedItemNames: form.relatedItemNames,
      ...options,
    });
  }

  openFormFilloutModalWithData(data: CartFormsModalsFilloutData) {
    return this.modalService.open<TODO>({
      size: 'full',
      component: 'acmeCartFormsModalFillout',
      backdrop: 'static',
      resolve: {
        MODAL_DATA: () => data,
      },
    });
  }
}

export const CartFormsServiceModule = angular
  .module('acme.view.application.cart.components.cart-forms.service', [ModalServiceModule])
  .service('CartFormsService', CartFormsService).name;
